import { useEffect, useMemo, useState } from "react";
import { deadlines, editsMasterData, restockAuto, restockNeed } from "@lagerlog/core";
import { useCatalog, useLedger, useStore } from "../hooks.ts";
import type { Article } from "../catalog.ts";
import { fmt } from "./Book.tsx";
import { usePantryStock } from "./Pantry.tsx";
import { Emoji } from "./Emoji.tsx";
import { reservedOf } from "./Reserved.tsx";
import { failText, when } from "./EinkaufLink.tsx";
import type { EinkaufState } from "./EinkaufLink.tsx";

const date = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
const CHECKED_KEY = "lagerlog.restock-checked";

function readChecked(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(CHECKED_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

/** One part per line in the right column, as everywhere; a note grey on its own line below. */
function Side({ text, note }: { text: string; note?: string }) {
  return (
    <span className="side">
      {text.split(" + ").map((part, i, all) => (
        <span key={i}>{i < all.length - 1 ? `${part} +` : part}</span>
      ))}
      {note && <span className="muted">{note}</span>}
    </span>
  );
}

/** The last send by itself (F19): when, and what it did, or why it did not go. */
function AutoSent({ sent }: { sent: NonNullable<NonNullable<EinkaufState["link"]>["lastAuto"]> }) {
  const parts = [sent.added ? `${sent.added} neu` : "", sent.updated ? `${sent.updated} geändert` : "", sent.removed ? `${sent.removed} entfernt` : ""].filter(Boolean);
  return (
    <p className={sent.ok ? "muted" : "warn"} data-testid="restock-auto">
      {sent.ok
        ? `Automatisch abgeglichen ${when(sent.at)}${parts.length ? `: ${parts.join(", ")}` : ""}.`
        : `Automatisch nicht gesendet (${when(sent.at)}): ${sent.error ?? "keine Antwort"}. VorratsBuch versucht es bei der nächsten Änderung wieder.`}
    </p>
  );
}

/**
 * The start of VorratsBuch (docs/18-vorratsbuch.md, V0-6): what expires soon and what to buy again.
 * The list to buy is the app's own until the shopping list is linked (V1): tick off, share as text.
 */
export function Home() {
  const { store, version } = useStore();
  const catalog = useCatalog();
  const ledger = useLedger();
  const stock = usePantryStock();
  const [checked, setChecked] = useState(readChecked);
  const [shared, setShared] = useState<string | null>(null);
  // The link to the EinkaufsZettel (V1): where „Nachkaufen“ goes, if linked.
  const keeper = editsMasterData(store.rights().role);
  const [einkauf, setEinkauf] = useState<EinkaufState["link"]>(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!keeper) return;
    store
      .request<EinkaufState>("/api/workspace/einkauf")
      .then((s) => setEinkauf(s.link))
      .catch(() => setEinkauf(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const soon = useMemo(() => {
    if (!catalog || !ledger) return [];
    const scope = new Set(store.config?.lagers ?? []);
    return deadlines(ledger, (id) => catalog.articles.get(id)?.fields, Date.now(), 7).filter((d) => d.role === "expiry" && scope.has(d.lager) && catalog.articles.has(d.article));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, ledger, version]);

  const toBuy = useMemo(() => {
    if (!catalog) return [];
    const out: { a: Article; need: number }[] = [];
    // What a recipe holds is not there to use (V3): restock counts with what is available.
    const held = reservedOf(catalog).byArticle;
    for (const a of catalog.articles.values()) {
      const need = restockNeed((stock.get(a.id)?.qty ?? 0) - (held.get(a.id) ?? 0), a.fields);
      if (need > 0) out.push({ a, need });
    }
    return out.sort((x, y) => x.a.name.localeCompare(y.a.name, "de"));
  }, [catalog, stock]);

  if (!catalog || !ledger) return null;

  const toggle = (id: string) => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChecked(next);
    try {
      localStorage.setItem(CHECKED_KEY, JSON.stringify([...next]));
    } catch {
      // Only a convenience.
    }
  };
  const lineOf = ({ a, need }: { a: Article; need: number }) => `${fmt(a, need * a.bottle)} ${a.name}`.replace(/\s+\(.*\)$/, "");
  /** The whole need to the shopping list, worked out by the server: what is filled up since is taken off there. */
  async function send() {
    setSending(true);
    setShared(null);
    try {
      const r = await store.request<{ added?: string[]; updated?: string[]; removed?: string[] }>("/api/workspace/einkauf/stock", {
        method: "POST",
        body: "{}",
      });
      const list = einkauf?.lists.find((l) => l.id === einkauf.list)?.name ?? "die Einkaufsliste";
      const parts = [r.added?.length ? `${r.added.length} neu` : "", r.updated?.length ? `${r.updated.length} geändert` : "", r.removed?.length ? `${r.removed.length} entfernt` : ""].filter(Boolean);
      setShared(`An „${list}“ gesendet${parts.length ? `: ${parts.join(", ")}` : ", nichts Neues"}.`);
    } catch (e) {
      setShared(failText(e));
    } finally {
      setSending(false);
    }
  }

  async function share() {
    const open = toBuy.filter((r) => !checked.has(r.a.id));
    const text = open.map((r) => `– ${lineOf(r)}`).join("\n");
    try {
      if (navigator.share) await navigator.share({ title: "Nachkaufen", text });
      else {
        await navigator.clipboard.writeText(text);
        setShared("In die Zwischenablage kopiert.");
      }
    } catch {
      // Cancelled by the person.
    }
  }

  return (
    <div className="home">
      {catalog && catalog.articles.size === 0 && (
        // A new household: one way to begin (C2).
        <section className="card" data-testid="home-first">
          <strong>Noch nichts im Vorrat</strong>
          {keeper ? (
            <>
              <p>Leg deinen ersten Vorrat an. Einen Einkauf als Rechnung oder Tabelle liest „Beleg einlesen“ unten ein.</p>
              <div className="buttons">
                <a className="button primary" href="#/vorraete?neu" data-testid="home-first-new">
                  + Erster Vorrat
                </a>
              </div>
            </>
          ) : (
            <p className="muted">Sobald jemand im Haushalt etwas anlegt, steht es hier.</p>
          )}
        </section>
      )}
      <section className="card" data-testid="home-soon">
        <div className="row between">
          <strong>Läuft bald ab</strong>
          <a className="link" href="#/fristen" data-testid="home-deadlines">
            Alle Fristen →
          </a>
        </div>
        {soon.length === 0 ? (
          <p className="muted">Diese Woche läuft nichts ab.</p>
        ) : (
          <ul className="list">
            {soon.slice(0, 8).map((d) => {
              const a = catalog.articles.get(d.article)!;
              return (
                <li key={`${d.article}|${d.role === "expiry" ? d.lot : ""}`} className={`row between ${d.state === "expired" ? "negative" : ""}`}>
                  <span className="named-emoji">
                    <Emoji fields={a.fields} />
                    <span className="text">
                    {a.name}
                    <br />
                    <span className="muted">{d.role === "expiry" ? `${d.state === "expired" ? "abgelaufen am" : "MHD"} ${date(d.date)} · ${catalog.lagers.get(d.lager)?.name ?? ""}` : ""}</span>
                    </span>
                  </span>
                  <Side text={fmt(a, d.qty)} />
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <section className="card" data-testid="home-restock">
        <div className="row between">
          <strong>Nachkaufen</strong>
          {toBuy.length > 0 && !einkauf?.list && (
            <button className="link" onClick={() => void share()} data-testid="restock-share">
              Als Text teilen
            </button>
          )}
        </div>
        {toBuy.length === 0 ? (
          <p className="muted">Nichts nachzukaufen. Was „Nachkaufen ab“ erreicht, steht hier.</p>
        ) : (
          <ul className="list">
            {toBuy.map((r) => (
              <li key={r.a.id}>
                <label className={`check row between ${checked.has(r.a.id) ? "muted" : ""}`}>
                  <span className="named-emoji">
                    <input type="checkbox" checked={checked.has(r.a.id)} onChange={() => toggle(r.a.id)} />
                    <Emoji fields={r.a.fields} />
                    <span className="text">
                      {r.a.name}
                      <br />
                      <span className="muted">da: {stock.get(r.a.id)?.qty ? fmt(r.a, stock.get(r.a.id)!.qty) : "nichts"}</span>
                    </span>
                  </span>
                  <Side text={fmt(r.a, r.need * r.a.bottle)} note={einkauf?.list && restockAuto(r.a.fields) ? "automatisch" : undefined} />
                </label>
              </li>
            ))}
          </ul>
        )}
        {einkauf?.list && (
          <button className="primary wide" disabled={sending} onClick={() => void send()} data-testid="restock-send">
            {toBuy.length ? "An EinkaufsZettel senden" : "EinkaufsZettel abgleichen"}
          </button>
        )}
        {shared && (
          <p className="muted" data-testid="restock-sent">
            {shared}
          </p>
        )}
        {einkauf?.list && einkauf.lastAuto && <AutoSent sent={einkauf.lastAuto} />}
        <p className="hint">
          {einkauf?.list
            ? "Gesendet wird immer der ganze Bedarf: Was inzwischen aufgefüllt ist, verschwindet auch von der Einkaufsliste. Was „immer automatisch“ ist oder schon gesendet wurde, gleicht VorratsBuch von selbst ab. Gekauftes kommt über den Eingang zurück."
            : "Abgehakt heißt „liegt im Wagen“. Eingelagert wird beim Buchen; dann verschwindet es von hier."}
        </p>
      </section>
      <div className="buttons">
        <a className="button primary" href="#/buchen?einlagern">
          Einlagern
        </a>
        <a className="button" href="#/buchen">
          Verbrauchen
        </a>
        {editsMasterData(store.rights().role) && (
          <a className="button" href="#/eingang" data-testid="home-inbound">
            Beleg einlesen
          </a>
        )}
      </div>
    </div>
  );
}
